import { useSupabase } from '@/hooks/useSupabase'
import { supabase } from '@/lib/supabase'
import type { HealthScore } from '@/types'

export function useHealth() {
  return useSupabase<HealthScore | null>(async () => {
    const { data, error } = await supabase
      .from('health_scores')
      .select('*')
      .order('calculated_at', { ascending: false })
      .limit(1)
      .single()

    if (error && error.code !== 'PGRST116') { // Not found error
      throw error
    }
    
    return data || null
  })
}

export function useHealthHistory(limit = 30) {
  const dependencies = [limit]
  
  return useSupabase<HealthScore[]>(async () => {
    const { data, error } = await supabase
      .from('health_scores')
      .select('*')
      .order('calculated_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }, dependencies)
}

export function useHealthMetrics() {
  return useSupabase<{
    domain_coverage: number
    avg_depth: number
    connectivity: number
    contradictions_resolved: number
    total_concepts: number
    mesh_concepts: number
    connection_density: number
    emergences_count: number
  }>(async () => {
    // Get latest health score
    const { data: healthData } = await supabase
      .from('health_scores')
      .select('*')
      .order('calculated_at', { ascending: false })
      .limit(1)
      .single()

    // Get additional metrics
    const [conceptsResult, meshResult, connectionsResult, emergencesResult] = await Promise.all([
      supabase.from('concepts').select('id', { count: 'exact' }),
      supabase.from('concepts').select('id', { count: 'exact' }).eq('mesh_eligible', true),
      supabase.from('connections').select('id', { count: 'exact' }),
      supabase.from('emergences').select('id', { count: 'exact' }).eq('status', 'new')
    ])

    const total_concepts = conceptsResult.count || 0
    const mesh_concepts = meshResult.count || 0
    const total_connections = connectionsResult.count || 0
    const emergences_count = emergencesResult.count || 0

    // Calculate connection density
    const connection_density = total_concepts > 0 
      ? (total_connections / (total_concepts * (total_concepts - 1))) * 100 
      : 0

    return {
      domain_coverage: healthData?.domain_coverage || 0,
      avg_depth: healthData?.avg_depth || 0,
      connectivity: healthData?.connectivity || 0,
      contradictions_resolved: healthData?.contradictions_resolved || 0,
      total_concepts,
      mesh_concepts,
      connection_density: Math.round(connection_density * 100) / 100,
      emergences_count
    }
  })
}

// Hook for triggering health score recalculation
export function useRecalculateHealth() {
  const recalculate = async () => {
    // This would typically call a stored procedure or function
    // For now, we'll just get fresh data
    const { data, error } = await supabase
      .rpc('calculate_health_score')

    if (error) throw error
    return data
  }

  return { recalculate }
}

// Hook for health insights and recommendations
export function useHealthInsights() {
  return useSupabase<{
    insights: string[]
    recommendations: string[]
    top_action?: string
  }>(async () => {
    const { data: healthData } = await supabase
      .from('health_scores')
      .select('*')
      .order('calculated_at', { ascending: false })
      .limit(1)
      .single()

    // Generate insights based on scores
    const insights: string[] = []
    const recommendations: string[] = []

    if (healthData) {
      const norm = (v: number) => v > 1 ? v / 100 : v
      if (norm(healthData.domain_coverage) < 0.6) {
        insights.push('Cobertura de domínios baixa - conhecimento concentrado')
        recommendations.push('Explore novos domínios de conhecimento')
      }

      if (norm(healthData.avg_depth) < 0.4) {
        insights.push('Profundidade média baixa - conhecimento superficial')
        recommendations.push('Aprofunde-se em conceitos existentes')
      }

      if (norm(healthData.connectivity) < 0.4) {
        insights.push('Conectividade baixa - ideias isoladas')
        recommendations.push('Busque conexões entre conceitos diferentes')
      }

      if (norm(healthData.contradictions_resolved) < 0.1) {
        insights.push('Poucas contradições resolvidas')
        recommendations.push('Explore paradoxos e tensões em seu conhecimento')
      }
    }

    // Default insights if no data
    if (insights.length === 0) {
      insights.push('Sistema de conhecimento em desenvolvimento')
      recommendations.push('Continue adicionando conteúdo diversificado')
    }

    return {
      insights,
      recommendations,
      top_action: healthData?.top_action || recommendations[0]
    }
  })
}